import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const FIO_CHECK_INTERVAL = 31;
const CHECKING_DURATION_MS = 2_000;

interface UsePaymentTimerDeps {
  showTimer: boolean;
  checkInterval?: number;
  monitoringStartTime: number | null;
  paymentId?: string | null;
}

interface UsePaymentTimerResult {
  timeUntilNextCheck: number;
  isChecking: boolean;
}

/**
 * Wall-clock countdown for the next bank-status check.
 *
 * Critical for mobile: browsers freeze `setInterval` while the tab is hidden
 * (user opens banking app). Always derive seconds from Date.now() + an anchor,
 * and resync immediately on `visibilitychange` / `pageshow` / `focus`.
 */
export function usePaymentTimer({
  showTimer,
  checkInterval = FIO_CHECK_INTERVAL,
  monitoringStartTime,
  paymentId,
}: UsePaymentTimerDeps): UsePaymentTimerResult {
  const [timeUntilNextCheck, setTimeUntilNextCheck] = useState<number>(checkInterval);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  const checkIntervalRef = useRef(checkInterval);
  const monitoringStartTimeRef = useRef(monitoringStartTime);
  /** Prefer last SSE check event; else monitoring start; else local cycle start. */
  const anchorTimeRef = useRef<number | null>(null);
  const isCheckingRef = useRef(false);
  const checkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    checkIntervalRef.current = checkInterval;
    monitoringStartTimeRef.current = monitoringStartTime;
  }, [checkInterval, monitoringStartTime]);

  const clearCheckingTimeout = useCallback((): void => {
    if (checkingTimeoutRef.current !== null) {
      clearTimeout(checkingTimeoutRef.current);
      checkingTimeoutRef.current = null;
    }
  }, []);

  const pulseChecking = useCallback((): void => {
    if (isCheckingRef.current) {
      return;
    }
    isCheckingRef.current = true;
    setIsChecking(true);
    clearCheckingTimeout();
    checkingTimeoutRef.current = setTimeout(() => {
      isCheckingRef.current = false;
      setIsChecking(false);
      checkingTimeoutRef.current = null;
    }, CHECKING_DURATION_MS);
  }, [clearCheckingTimeout]);

  const secondsUntilNextFromAnchor = useCallback((anchor: number, nowMs: number): number => {
    const intervalMs = checkIntervalRef.current * 1000;
    const elapsed = Math.max(0, nowMs - anchor);
    const remainingMs = intervalMs - (elapsed % intervalMs);
    if (elapsed > 0 && elapsed % intervalMs === 0) {
      return 0;
    }
    return Math.max(0, Math.ceil(remainingMs / 1000));
  }, []);

  const syncFromWallClock = useCallback((): void => {
    const interval = checkIntervalRef.current;
    const now = Date.now();
    let anchor = anchorTimeRef.current;
    if (anchor === null) {
      anchor = now;
      anchorTimeRef.current = anchor;
      setTimeUntilNextCheck(interval);
      return;
    }
    const seconds = secondsUntilNextFromAnchor(anchor, now);
    setTimeUntilNextCheck(seconds > 0 ? seconds : interval);
    if (seconds <= 1) {
      pulseChecking();
      if (seconds === 0) {
        anchorTimeRef.current = now;
        setTimeUntilNextCheck(interval);
      }
    }
  }, [pulseChecking, secondsUntilNextFromAnchor]);

  // Keep anchor aligned with server monitoring start until an SSE check event arrives.
  useEffect(() => {
    if (monitoringStartTime === null) {
      return;
    }
    if (
      anchorTimeRef.current !== null &&
      anchorTimeRef.current >= monitoringStartTime
    ) {
      return;
    }
    anchorTimeRef.current = monitoringStartTime;
    if (!showTimer) {
      return;
    }
    // Defer setState — react-hooks/set-state-in-effect.
    const timer = window.setTimeout(() => {
      syncFromWallClock();
    }, 0);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [monitoringStartTime, showTimer, syncFromWallClock]);

  // Tick + visibility / pageshow / focus resync (isChecking must NOT be an effect dep —
  // that used to clear the interval and freeze the countdown).
  useEffect(() => {
    if (!showTimer) {
      clearCheckingTimeout();
      isCheckingRef.current = false;
      // Defer setState — react-hooks/set-state-in-effect.
      const resetTimer = window.setTimeout(() => {
        setIsChecking(false);
      }, 0);
      return (): void => {
        window.clearTimeout(resetTimer);
      };
    }

    anchorTimeRef.current ??= monitoringStartTimeRef.current ?? Date.now();

    const intervalId = setInterval(() => {
      syncFromWallClock();
    }, 1000);

    const resyncIfVisible = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      syncFromWallClock();
    };

    // Initial sync in macrotask (not sync in effect body).
    const initialSyncTimer = window.setTimeout(() => {
      syncFromWallClock();
    }, 0);

    document.addEventListener('visibilitychange', resyncIfVisible);
    window.addEventListener('pageshow', resyncIfVisible);
    window.addEventListener('focus', resyncIfVisible);

    return (): void => {
      window.clearTimeout(initialSyncTimer);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', resyncIfVisible);
      window.removeEventListener('pageshow', resyncIfVisible);
      window.removeEventListener('focus', resyncIfVisible);
    };
  }, [showTimer, checkInterval, monitoringStartTime, syncFromWallClock, clearCheckingTimeout]);

  useEffect(() => {
    return (): void => {
      clearCheckingTimeout();
    };
  }, [clearCheckingTimeout]);

  useEffect(() => {
    if (!showTimer || paymentId === undefined || paymentId === null) {
      return;
    }

    const handleCheckStarted = (event: Event): void => {
      const customEvent = event as CustomEvent<{ paymentId?: string; checkTime?: number }>;
      const eventPaymentId = customEvent.detail?.paymentId;
      if (eventPaymentId !== paymentId) {
        return;
      }
      const checkTime = customEvent.detail?.checkTime ?? Date.now();
      anchorTimeRef.current = checkTime;
      setTimeUntilNextCheck(checkIntervalRef.current);
      pulseChecking();
    };

    window.addEventListener('payment-check-started', handleCheckStarted);
    return (): void => {
      window.removeEventListener('payment-check-started', handleCheckStarted);
    };
  }, [showTimer, paymentId, pulseChecking]);

  return { timeUntilNextCheck, isChecking };
}
